import React from "react";
import { AHWidgetLoading } from "arkh";
import { graphql, useFragment } from "react-relay";
import { BaseWidgetProps } from "./BaseProps";
import { WidgetIntegrationPushAppy } from "./WidgetIntegrationPushAppy/WidgetIntegrationPushAppy";
import { WidgetIpoolIntegration } from "./WidgetIpoolIntegration/WidgetIpoolIntegration";
import { WidgetIssueOverview } from "./WidgetIssueOverview/WidgetIssueOverview";

import { WidgetRoutineOverview } from "./WidgetRoutineOverview/WidgetRoutineOverview";
import { WidgetRoutinePositivePercentage } from "./WidgetRoutinePositivePercentage/WidgetRoutinePositivePercentage";
import { WidgetTaskCollection } from "./WidgetTaskCollection/WidgetTaskCollection";
import { WidgetTaskSingle } from "./WidgetTaskSingle/WidgetTaskSingle";
import { WidgetTeamSchedule } from "./WidgetTeamSchedule/WidgetTeamSchedule";
import { WidgetTodoOverview } from "./WidgetTodoOverview/WidgetTodoOverview";
import { WidgetWeather } from "./WidgetWeather/WidgetWeather";
import { AllWidgetsContent_widget$key } from "./__generated__/AllWidgetsContent_widget.graphql";
import { AllWidgets_widget$key } from "./__generated__/AllWidgets_widget.graphql";
import { WidgetEmployeeSchedule } from "./WidgetEmployeeSchedule/WidgetEmployeeSchedule";
import { WidgetIssueSummary } from "./WidgetIssueSummary/WidgetIssueSummary";
import { WidgetIntegrationTurnpike } from "./WidgetIntegrationTurnpike/WidgetIntegrationTurnpike";
import { WidgetCalendar } from "./WidgetCalendar/WidgetCalendar";
import { WidgetFile } from "./WidgetFile/WidgetFile";
import { WidgetIntegrationFlourishPraise } from "./WidgetIntegrationFlourishPraise/WidgetIntegrationFlourishPraise";
import { WidgetNote } from "./WidgetNote";
import { WidgetSpace } from "./WidgetSpace/WidgetSpace";

function AllWidgetsContent(
  props: BaseWidgetProps & {
    spaceFocusId?: string;
    widget: AllWidgetsContent_widget$key;
    margin: number;
  }
) {
  const widget = useFragment(
    graphql`
      fragment AllWidgetsContent_widget on Widget {
        ... on WidgetSpace {
          ...WidgetSpace_widgetSpace
          __typename
        }
        ... on WidgetIntegrationFlourishPraise {
          ...WidgetIntegrationFlourishPraise_widgetIntegrationFlourishPraise
          __typename
        }
        ... on WidgetFile {
          ...WidgetFile_widgetFile
          __typename
        }
        ... on WidgetTeamSchedule {
          ...WidgetTeamSchedule_widget
          __typename
        }
        ... on WidgetEmployeeSchedule {
          ...WidgetEmployeeSchedule_widgetEmployeeSchedule
          __typename
        }
        ... on WidgetWeather {
          ...WidgetWeather_widget
          __typename
        }
        ... on WidgetTodoOverview {
          ...WidgetTodoOverview_widget
          __typename
        }
        ... on WidgetNote {
          ...WidgetNote_widgetNote
          __typename
        }
        ... on WidgetTaskSingle {
          ...WidgetTaskSingle_widgetTaskSingle
          __typename
        }
        ... on WidgetTaskCollection {
          ...WidgetTaskCollection_widgetTaskCollection
          __typename
        }
        ... on WidgetRoutineOverview {
          ...WidgetRoutineOverview_widgetRoutineOverview
          __typename
        }
        ... on WidgetRoutinePositivePercentage {
          ...WidgetRoutinePositivePercentage_widgetRoutinePositivePercentage
          __typename
        }
        ... on WidgetIssueOverview {
          ...WidgetIssueOverview_widgetIssueOverview
          __typename
        }
        ... on WidgetIntegrationIpool {
          ...WidgetIpoolIntegration_widgetIntegrationIpool
          __typename
        }
        ... on WidgetIntegrationPushAppy {
          ...WidgetIntegrationPushAppy_widgetIntegrationPushAppy
          __typename
        }
        ... on WidgetIssueSummary {
          ...WidgetIssueSummary_widgetIssueSummary
          __typename
        }
        ... on WidgetIntegrationTurnpike {
          ...WidgetIntegrationTurnpike_widgetIntegrationTurnpike
          __typename
        }
        ... on WidgetCalendar {
          ...WidgetCalendar_widgetCalendar
          __typename
        }
      }
    `,
    props.widget
  );

  switch (widget.__typename) {
    case "WidgetIntegrationFlourishPraise":
      return (
        <WidgetIntegrationFlourishPraise
          widget={widget}
          mode={props.mode}
          interactive={props.interactive}
          storecastId={props.storecastId}
        />
      );
    case "WidgetFile":
      return (
        <WidgetFile
          widget={widget}
          onEvent={props.onEvent}
          mode={props.mode}
          interactive={props.interactive}
          isFocused={props.isFocused}
          storecastId={props.storecastId}
          idiom={props.idiom}
        />
      );
    case "WidgetTeamSchedule":
      return (
        <WidgetTeamSchedule
          widget={widget}
          onEvent={props.onEvent}
          mode={props.mode}
          interactive={props.interactive}
          isFocused={props.isFocused}
          storecastId={props.storecastId}
          highlightedItemId={props.highlightedItemId}
          idiom={props.idiom}
        />
      );

    case "WidgetWeather":
      return (
        <WidgetWeather
          widget={widget}
          mode={props.mode}
          storecastId={props.storecastId}
        />
      );
    case "WidgetTodoOverview":
      return (
        <WidgetTodoOverview
          widget={widget}
          onEvent={props.onEvent}
          mode={props.mode}
          interactive={props.interactive}
          isFocused={props.isFocused}
          storecastId={props.storecastId}
          fetchKey={props.fetchKey}
          highlightedItemId={props.highlightedItemId}
          idiom={props.idiom}
        />
      );
    case "WidgetNote":
      return (
        <WidgetNote
          widget={widget}
          onEvent={props.onEvent}
          mode={props.mode}
          interactive={props.interactive}
          isFocused={props.isFocused}
          storecastId={props.storecastId}
          idiom={props.idiom}
        />
      );
    case "WidgetTaskSingle":
      return (
        <WidgetTaskSingle
          widget={widget}
          onEvent={props.onEvent}
          mode={props.mode}
          interactive={props.interactive}
          isFocused={props.isFocused}
          storecastId={props.storecastId}
        />
      );
    case "WidgetTaskCollection":
      return (
        <WidgetTaskCollection
          widget={widget}
          onEvent={props.onEvent}
          mode={props.mode}
          interactive={props.interactive}
          isFocused={props.isFocused}
          storecastId={props.storecastId}
        />
      );
    case "WidgetRoutineOverview":
      return (
        <WidgetRoutineOverview
          widget={widget}
          onEvent={props.onEvent}
          mode={props.mode}
          interactive={props.interactive}
          isFocused={props.isFocused}
          storecastId={props.storecastId}
        />
      );
    case "WidgetRoutinePositivePercentage":
      return (
        <WidgetRoutinePositivePercentage
          widget={widget}
          onEvent={props.onEvent}
          mode={props.mode}
          interactive={props.interactive}
          isFocused={props.isFocused}
          storecastId={props.storecastId}
        />
      );
    case "WidgetIssueOverview":
      return (
        <WidgetIssueOverview
          widget={widget}
          onEvent={props.onEvent}
          mode={props.mode}
          interactive={props.interactive}
          isFocused={props.isFocused}
          storecastId={props.storecastId}
          highlightedItemId={props.highlightedItemId}
        />
      );
    case "WidgetIntegrationIpool":
      return (
        <WidgetIpoolIntegration
          widget={widget}
          mode={props.mode}
          interactive={props.interactive}
          storecastId={props.storecastId}
        />
      );
    case "WidgetIntegrationPushAppy":
      return (
        <WidgetIntegrationPushAppy
          widget={widget}
          mode={props.mode}
          interactive={props.interactive}
          storecastId={props.storecastId}
        />
      );

    case "WidgetIntegrationTurnpike":
      return (
        <WidgetIntegrationTurnpike
          widget={widget}
          mode={props.mode}
          interactive={props.interactive}
          storecastId={props.storecastId}
        />
      );
    case "WidgetEmployeeSchedule":
      return (
        <WidgetEmployeeSchedule
          widget={widget}
          mode={props.mode}
          interactive={props.interactive}
          storecastId={props.storecastId}
        />
      );
    case "WidgetIssueSummary":
      return (
        <WidgetIssueSummary
          widget={widget}
          mode={props.mode}
          interactive={props.interactive}
          storecastId={props.storecastId}
          onEvent={props.onEvent}
        />
      );
    case "WidgetCalendar":
      return (
        <WidgetCalendar
          widget={widget}
          mode={props.mode}
          interactive={props.interactive}
          storecastId={props.storecastId}
        />
      );
    case "WidgetSpace":
      return (
        <WidgetSpace
          {...props}
          widget={widget}
          margin={props.margin}
          spaceFocusId={props.spaceFocusId}
        />
      );
  }
  return null;
}

function AllWidgetsSuspensed(
  props: BaseWidgetProps & {
    spaceFocusId?: string;
    widget: AllWidgets_widget$key;
    margin: number;
  }
) {
  const widget = useFragment(
    graphql`
      fragment AllWidgets_widget on Widget {
        id
        title
        ...AllWidgetsContent_widget
      }
    `,
    props.widget
  );

  return (
    <React.Suspense fallback={<AHWidgetLoading title={widget.title} />}>
      <AllWidgetsContent
        {...props}
        widget={widget}
        spaceFocusId={props.spaceFocusId}
      />
    </React.Suspense>
  );
}

const Widget = React.memo(AllWidgetsSuspensed);

export {
  WidgetIntegrationPushAppy,
  WidgetIpoolIntegration,
  WidgetIssueOverview,
  WidgetNote,
  WidgetRoutineOverview,
  WidgetRoutinePositivePercentage,
  WidgetTaskCollection,
  WidgetTaskSingle,
  WidgetTeamSchedule,
  WidgetTodoOverview,
  WidgetWeather,
  Widget as AllWidgets,
};
